import type {InitiativeProposal} from "~/sit/chat/initiativeProposal";

export type {InitiativeProposal} from "~/sit/chat/initiativeProposal";

export type AgentOption = {
    /**
   * This is the name of the Agent (defined in the BE)
   */
    appName: string;
    label: string;
    description: string;
};

/** Leaf prompt in a `one_layer` template. */
export type AgentTemplatePrompt = {
    title: string;
    prompt: string;
};

/** Node in a `two_layer` template (group or leaf). */
export type AgentTemplateMember = {
    title: string;
    prompt?: string;
    members?: AgentTemplateMember[];
};

/** Template group from `GET /api/v1/agents-metadata` (per agent). */
export type AgentTemplateOneLayer = {
    name: string;
    title: string;
    template_type: "one_layer";
    prompts: AgentTemplatePrompt[] | string[]; // string[] is for the old templates
};

export type AgentTemplateTwoLayer = {
    name: string;
    title: string;
    template_type: "two_layer";
    members: AgentTemplateMember[];
};

export type AgentTemplate = AgentTemplateOneLayer | AgentTemplateTwoLayer;

/** Response item from the agent chat backend `GET /api/v1/agents-metadata`. */
export type AgentMetadata = {
    app_name: string;
    display_name: string;
    description: string;
    templates?: AgentTemplate[];
    input_placeholder: string;
    sort_order: number;
    enabled: boolean;
    active_databags: number[];
};

/** Session state key written by tail-spend consolidation tools (ADK state_delta). */
export const CONSOLIDATION_PROGRESS_STATE_KEY =
    "temp:tail_spend_consolidation_progress" as const;

/** Tail-spend consolidation tools that report progress through `CONSOLIDATION_PROGRESS_STATE_KEY`. */
export const CONSOLIDATION_TOOL_NAMES: ReadonlySet<string> = new Set([
    "consolidate_suppliers",
    "consolidate_all_tail_suppliers",
]);

export type ConsolidationToolProgress = {
    stage: string;
    completed: number;
    total: number;
    detail?: string;
    /** 1–4 macro phase from backend (opportunities → initiatives → persist → complete). */
    stageNumber?: number;
    stageTotal?: number;
};

/**
 * Embedded Superset dashboard metadata emitted by dashgen tool results
 * (`functionResponse.response.embedded`). Guest tokens are fetched separately
 * from `GET /api/v1/superset/guest-token?dashboard_uuid={uuid}`.
 */
export type EmbeddedDashboard = {
    /** Superset embedded-dashboard uuid (NOT the numeric dashboard id). */
    uuid: string;
    /** Browser-facing Superset origin the iframe loads from. */
    superset_domain: string;
    dashboard_id: number;
    title: string;
};

/** Row from `GET /api/v1/superset/dashboards` — a dashgen dashboard owned by the user. */
export type UserDashboardRecord = {
    dashboard_id: number;
    title: string | null;
    url: string | null;
    embedded_uuid: string | null;
    /** Originating chat session — deep-link target for opening the dashboard. */
    session_id: string;
    app_name: string;
    databag_id: number;
    created_at: string;
    updated_at: string;
};

/**
 * EmbeddedDashboard as shown in the side panel. `refreshToken` bumps whenever
 * the agent creates/edits the dashboard (its embed uuid stays stable across
 * edits), forcing the iframe to remount and show the new charts.
 */
export type EmbeddedDashboardView = EmbeddedDashboard & {
    refreshToken: number;
};

/** A paused `adk_request_input`-style long-running tool call awaiting a human reply. */
export type PendingInputRequest = {
    /** Matches the originating functionCall id, echoed back in the resuming functionResponse. */
    callId: string;
    toolName: string;
};

export type ChatMessage = {
    id: string;
    type: "user" | "bot";
    content: string;
    /**
     * "thought" collects every `thought: true` part of one agent turn into a single section. "stopped"
     * marks where the user stopped the turn; it ends the turn but renders no bubble.
     */
    event_type?: "text" | "thought" | "function_call" | "function_response" | "request_input" | "stopped";
    /** ADK agent that produced this message (`event.author`); sub-agents report their own name. */
    author?: string;
    /** ADK invocation id for the event this bubble came from (for feedback API). */
    invocationId?: string;
    timestamp: Date;
    /** Bot text still streaming: built from partial ADK chunks until the response's final event arrives. */
    isPartial?: boolean;
    /** Populated from ADK state_delta during tail-spend consolidation. */
    toolProgress?: ConsolidationToolProgress;
    /** Superset dashboard produced by this tool response (dashgen). */
    embedded?: EmbeddedDashboard;
    /** Uncommitted initiative draft from `propose_initiative`. */
    initiativeProposal?: InitiativeProposal;
    /** Derived by `placeInitiativeDrafts`: a newer draft exists, so this one is read-only. */
    initiativeSuperseded?: boolean;
    /** Present when event_type is "request_input" — identifies the paused tool call. */
    requestInput?: PendingInputRequest;
    /**
     * The originating functionCall/functionResponse id (function_call and function_response
     * messages only) — pairs each function_response with the call it resolves.
     */
    callId?: string;
    /** Answered tool calls only: when the function_response arrived (the call's `timestamp` is its start). */
    respondedAt?: Date;
    /**
     * The turn failed (it ends as "Failed"): the request failed, or ADK reported an error event
     * (`errorCode`), which is persisted, so the turn still reads as failed after a reload.
     */
    isError?: boolean;
    /**
     * Came from an event ADK counts as a final response (see isFinalResponse), so the turn may end on
     * it. A finished turn whose last reply lacks it was cut off, e.g. by a server crash that left no
     * error behind; it ends as "Failed".
     */
    endsTurn?: boolean;
    /**
     * Thought sections only: id of the transcript's last message at the latest append. A thought
     * arriving while that is still the last message continues the same paragraph.
     */
    thoughtTailId?: string;
    /**
     * Thought sections only: what each thought message (one per event, `<eventId>-thought`) added to
     * `content`, in order, with its leading separator. A reloaded section and the one that streamed
     * meanwhile begin at different events, so this tells mergeSnapshotWithStreamed which they share.
     */
    thoughtParts?: ThoughtPart[];
};

export type ThoughtPart = {id: string; text: string};

export type ChatSession = {
    id: string;
    userId: string;
    name: string;
    messages: ChatMessage[];
    created_at: Date;
    updated_at: Date;
    appName: string;
    /** Present when the session has an associated dashgen dashboard. */
    dashboard?: UserDashboardRecord | null;
};

// Backend session adapter response type
export type BackendChatSession = {
    session_id: string;
    user_id: string;
    last_update_time: string;
    databag_id: number;
    display_name: string;
    app_name: string;
    dashboard: UserDashboardRecord | null;
};

/** Response from `GET /api/v1/sessions/list`. */
export type SessionListResponse = {
    items: BackendChatSession[];
    total: number;
    limit: number;
    offset: number;
};

// ADK Event types

export type ADKContentPart = {
    text?: string;
    thought?: boolean;
    functionCall?: {id?: string; name: string; args: Record<string, any>};
    functionResponse?: {id?: string; name: string; response: any};
    codeExecutionResult?: unknown;
};

/** An ADK event in camelCase; normalizeADKEvent maps snake_case wire keys to these as events arrive. */
export type ADKEvent = {
    id: string;
    author: string;
    invocationId?: string;
    content?: {role: string; parts: ADKContentPart[]};
    actions?: {
        stateDelta?: Record<string, any>;
        artifactDelta?: Record<string, number>;
        /** A tool result shown as is, without the model summarizing it: it ends the agent's turn. */
        skipSummarization?: boolean;
    };
    partial?: boolean;
    timestamp?: number;
    /** ids (from ADKContentPart.functionCall.id) of function calls that are long-running / paused. */
    longRunningToolIds?: string[];
    /** Set when the model or a tool failed (e.g. `SAFETY_FILTER_TRIGGERED`); ADK persists the event. */
    errorCode?: string;
    errorMessage?: string;
};

/** `GET /sessions/{id}` with its events normalized (see normalizeADKEvent). */
export type ADKSessionDetail = {
    userId?: string;
    events: ADKEvent[];
};

export type ADKSession = {
    id: string;
    app_name: string;
    user_id: string;
    state: Record<string, any>;
    events: ADKEvent[];
    last_update_time: number;
};
